import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Params, Router, RouterModule } from '@angular/router';

import { Subject, finalize, takeUntil } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { SharedModule } from 'src/app/shared/shared.module';
import {
  AdminSessionApiService,
  AuditEvent,
  AuditTrailEntry,
  LoginActivity,
  PdSession,
} from 'src/app/core/services/admin-session-api.service';
import { assertSuccessEnvelope } from 'src/app/core/http/api-envelope.operators';
import {
  DEFAULT_LIST_MODULE_FILTERS,
  ListModuleFilters,
  ListModulePaginationMeta,
  ListModuleSurfaceComponent,
  ListModuleSurfaceConfig,
} from 'src/app/shared/components/list-module';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { AuditTrailTableComponent } from './audit-trail-table/audit-trail-table.component';
import { AuditEntryDrawerComponent } from '../audit-log/audit-entry-drawer.component';
import type { AuditLogRow } from 'src/app/core/services/audit-log-api.service';
import {
  AuditEntryType,
  isoDateDisplay,
  isoTimeDisplay,
  loginActivityRuntimeDisplay,
  loginActivityStatusClass,
  loginActivityStatusLabel,
  loginActivityUserEmail,
  loginActivityUserName,
  sessionAgentName,
  sessionEventLabel,
  sessionGeoDisplay,
  sessionScoreDisplay,
  sessionType,
} from './audit-trail-display.util';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { isSuperAdminSimulatePdSession } from 'src/app/shared/utils/pd-session-list.util';

const PAGE_SIZE = 15;

@Component({
  selector: 'app-audit-trail',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    SharedModule,
    MaterialModule,
    IconModule,
    PageSurfaceHeaderComponent,
    ListModuleSurfaceComponent,
    AuditTrailTableComponent,
    AuditEntryDrawerComponent,
    HasPermissionPipe,
  ],
  templateUrl: './audit-trail.component.html',
  styleUrl: './audit-trail.component.css',
})
export class AuditTrailComponent implements OnInit, OnDestroy {
  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;

  private readonly api = inject(AdminSessionApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroy$ = new Subject<void>();

  /**
   * Sign-ins have no PASS/REVIEW/FAIL verdict — the API maps PASS/FAIL onto
   * success/failed, and REVIEW returns nothing — so the filter is relabelled and
   * REVIEW dropped on that tab.
   */
  get listSurfaceConfig(): ListModuleSurfaceConfig {
    const isLogin = this.entryType === 'login';
    const isEvent = this.entryType === 'event';
    // An audited action succeeded or failed; like a sign-in it can never be under review, and it
    // is searched by what was done rather than by who it was done to.
    const outcomeOnly = isLogin || isEvent;
    return {
      toolbar: {
        search: {
          placeholder: this.searchPlaceholder(isLogin, isEvent),
          debounceMs: 350,
        },
        status: {
          options: outcomeOnly
            ? [
                { value: '', label: isEvent ? 'All Events' : 'All Sign-ins' },
                { value: 'PASS', label: 'Success' },
                { value: 'FAIL', label: 'Failed' },
              ]
            : [
                { value: '', label: 'All Results' },
                { value: 'PASS', label: 'Pass' },
                { value: 'REVIEW', label: 'Review' },
                { value: 'FAIL', label: 'Fail' },
              ],
        },
        dateRange: { placeholder: 'Date range' },
      },
      pagination: true,
    };
  }

  private searchPlaceholder(isLogin: boolean, isEvent: boolean): string {
    if (isEvent) return 'Search action, e.g. pd.session…';
    if (isLogin) return 'Search user, email…';
    return 'Search customer, agent…';
  }

  listFilters: ListModuleFilters = { ...DEFAULT_LIST_MODULE_FILTERS };

  entries: AuditTrailEntry[] = [];
  loading = false;
  error = '';
  page = 1;
  readonly pageSize = PAGE_SIZE;
  meta: ListModulePaginationMeta | null = null;

  /** When set (e.g. from Reports detail), list only this agent's sessions. */
  filterAgentId: number | null = null;

  detailLogin: LoginActivity | null = null;
  detailEvent: AuditEvent | null = null;

  /** Sessions and portal sign-ins are different records; the API splits on entry_type. */
  readonly entryTabs: { id: AuditEntryType; label: string }[] = [
    { id: '', label: 'All Activity' },
    { id: 'login', label: 'Portal Sign-ins' },
    // The audit chain: link creations, reschedules, cancellations, permission changes — recorded
    // all along and, until now, shown nowhere.
    { id: 'event', label: 'System Events' },
  ];
  entryType: AuditEntryType = '';

  setEntryType(next: AuditEntryType): void {
    if (this.entryType === next) return;
    this.entryType = next;
    // REVIEW belongs to PD sessions alone; neither a sign-in nor an audited action can be one.
    if ((next === 'login' || next === 'event') && this.listFilters.status === 'REVIEW') {
      this.listFilters = { ...this.listFilters, status: '' };
    }
    this.page = 1;
    this.syncQueryFromFilters();
    this.load();
  }

  ngOnInit(): void {
    if (this.redirectLegacySessionLink()) return;
    this.applyFiltersFromQuery();
    this.load();
  }

  /**
   * The session detail used to be a modal on this page, so callers still link here
   * as `/audit-trail?session=&tenant_id=&return=`. Forward those to the detail route
   * rather than making every caller change.
   */
  private redirectLegacySessionLink(): boolean {
    const q = this.route.snapshot.queryParamMap;
    const sessionId = Number(q.get('session'));
    if (!Number.isFinite(sessionId) || sessionId <= 0) return false;

    const tenantId = Number(q.get('tenant_id'));
    const back = (q.get('return') ?? '').trim();
    void this.router.navigate(['/audit-trail', 'session', sessionId], {
      queryParams: {
        ...(Number.isFinite(tenantId) && tenantId > 0 ? { tenant: String(tenantId) } : {}),
        ...(back.startsWith('/') ? { back } : {}),
      },
      replaceUrl: true,
    });
    return true;
  }

  private applyFiltersFromQuery(): void {
    const q = this.route.snapshot.queryParamMap;
    const agentId = Number(q.get('agent_id'));
    if (Number.isFinite(agentId) && agentId > 0) {
      this.filterAgentId = agentId;
    }
    const from = q.get('date_from');
    const to = q.get('date_to');
    if (from) this.listFilters.dateRangeStart = from;
    if (to) this.listFilters.dateRangeEnd = to;
    if (from && to) this.listFilters.dateRange = `${from} to ${to}`;

    const search = q.get('search');
    if (search) this.listFilters.searchQuery = search;
    const status = q.get('result');
    if (status) this.listFilters.status = status;
    const page = Number(q.get('page'));
    if (Number.isFinite(page) && page > 0) this.page = page;

    const entry = q.get('entry_type');
    if (entry === 'login' || entry === 'event') this.entryType = entry;
    // Legacy links used entry_type=session; that tab was removed — show merged timeline instead.
    if (entry === 'session') this.entryType = '';
  }

  /**
   * Filters live in the URL so returning from a session detail lands on the same
   * view, and so a filtered list is itself linkable.
   */
  private syncQueryFromFilters(): void {
    const params: Params = {
      search: this.listFilters.searchQuery.trim() || null,
      result: this.listFilters.status || null,
      date_from: this.listFilters.dateRangeStart || null,
      date_to: this.listFilters.dateRangeEnd || null,
      page: this.page > 1 ? this.page : null,
      agent_id: this.filterAgentId ?? null,
      entry_type: this.entryType || null,
    };
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data loading ───────────────────────────────────────────────────────────

  load(): void {
    this.loading = true;
    this.error = '';
    this.api
      .listAuditTrail({
        agent_id: this.filterAgentId ?? undefined,
        entry_type: this.entryType || undefined,
        search: this.listFilters.searchQuery.trim() || undefined,
        result: this.listFilters.status || undefined,
        date_from: this.listFilters.dateRangeStart || undefined,
        date_to: this.listFilters.dateRangeEnd || undefined,
        page: this.page,
        page_size: this.pageSize,
      })
      .pipe(assertSuccessEnvelope(), finalize(() => (this.loading = false)), takeUntil(this.destroy$))
      .subscribe({
        next: (env) => {
          const raw = env.data?.result ?? [];
          this.entries = raw.filter(
            (e) => e.kind !== 'session' || isSuperAdminSimulatePdSession(e.session),
          );
          const total = env.data?.total ?? 0;
          const lastPage = Math.max(1, Math.ceil(total / this.pageSize));
          this.meta = {
            total,
            lastPage,
            currentPage: this.page,
            perPage: this.pageSize,
          };
        },
        error: () => {
          this.error = 'Failed to load audit trail. Please try again.';
          this.entries = [];
          this.meta = null;
        },
      });
  }

  onListFiltersChange(patch: Partial<ListModuleFilters>): void {
    this.listFilters = { ...this.listFilters, ...patch };
    this.page = 1;
    this.syncQueryFromFilters();
    this.load();
  }

  onListPageChange(next: number): void {
    if (!this.meta || next < 1 || next > this.meta.lastPage) return;
    this.page = next;
    this.syncQueryFromFilters();
    this.load();
  }

  isSessionEntry(e: AuditTrailEntry): e is { kind: 'session'; sort_at: string; session: PdSession } {
    return e.kind === 'session';
  }

  /** Opens the routed detail page, carrying this list's URL so Back returns here. */
  openDetail(s: PdSession): void {
    void this.router.navigate(['/audit-trail', 'session', s.id], {
      queryParams: {
        ...(s.tenant_id != null && s.tenant_id > 0 ? { tenant: String(s.tenant_id) } : {}),
        back: this.router.url,
      },
    });
  }

  openLoginDetail(login: LoginActivity): void {
    this.detailLogin = login;
  }

  closeLoginDetail(): void {
    this.detailLogin = null;
  }

  /** Built once per open — a getter would hand the drawer a new object every CD pass and reset its toggles. */
  detailEventRow: AuditLogRow | null = null;

  openEventDetail(event: AuditEvent): void {
    this.detailEvent = event;
    this.detailEventRow = this.toAuditLogRow(event);
  }

  /** The trail's event shape is a subset of an audit-log row; fill the gaps so the shared drawer can render it. */
  private toAuditLogRow(e: AuditEvent): AuditLogRow {
    return {
      id: e.id,
      tenant_id: e.tenant_id ?? null,
      actor_id: e.actor_id ?? null,
      actor_type: e.actor_type ?? 'user',
      actor_email: e.actor_email,
      action: e.action,
      resource_type: e.resource_type,
      resource_id: e.resource_id,
      source: e.source,
      source_ip: e.source_ip ?? null,
      outcome: e.outcome ?? 'success',
      metadata: e.metadata ?? null,
      prev_hash: e.prev_hash ?? null,
      hash: e.hash ?? '',
      created_at: e.created_at,
      user_agent: e.user_agent ?? null,
      request_id: e.request_id ?? null,
      http_method: e.http_method ?? null,
      http_path: e.http_path ?? null,
    };
  }

  closeEventDetail(): void {
    this.detailEvent = null;
    this.detailEventRow = null;
  }

  /** 'pd_link #19' — the record the action touched. */
  eventResourceLabel(event: { resource_type: string | null; resource_id: string | null }): string {
    if (!event.resource_type) return '—';
    return event.resource_id ? `${event.resource_type} #${event.resource_id}` : event.resource_type;
  }



  @HostListener('document:keydown', ['$event'])
  onKeydown(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (this.detailLogin) this.closeLoginDetail();
    if (this.detailEvent) this.closeEventDetail();
  }

  entryDateDisplay(iso: string): string {
    return isoDateDisplay(iso);
  }

  entryTimeDisplay(iso: string): string {
    return isoTimeDisplay(iso);
  }

  readonly loginUserName = loginActivityUserName;
  readonly loginUserEmail = loginActivityUserEmail;
  readonly loginStatusLabel = loginActivityStatusLabel;
  readonly loginStatusClass = loginActivityStatusClass;
  readonly loginRuntimeDisplay = loginActivityRuntimeDisplay;


  // ── CSV Export ─────────────────────────────────────────────────────────────

  onExport(): void {
    const headers = ['Session ID', 'Date', 'Type', 'Agent', 'Customer', 'Event', 'Result', 'Score', 'Loan Type', 'Geo'];
    const rows = this.entries.map((e) => {
      if (this.isSessionEntry(e)) {
        const s = e.session;
        return [
          s.session_code ?? String(s.id),
          e.sort_at,
          sessionType(s),
          sessionAgentName(s),
          s.customer_name ?? '',
          sessionEventLabel(s),
          s.result ?? '',
          sessionScoreDisplay(s),
          s.loan_type ?? '',
          sessionGeoDisplay(s),
        ];
      }
      if (e.kind === 'event') {
        const ev = e.event;
        return [
          `event-${ev.id}`,
          e.sort_at,
          'System Event',
          ev.actor_email ?? ev.source,
          this.eventResourceLabel(ev),
          ev.action,
          ev.outcome ?? '',
          '—',
          '',
          '',
        ];
      }
      const l = e.login;
      return [
        `login-${l.id}`,
        e.sort_at,
        'Portal Login',
        this.loginUserName(l),
        this.loginUserEmail(l),
        'Sign-in',
        this.loginStatusLabel(l),
        '—',
        this.loginRuntimeDisplay(l),
        l.ip_address ?? '',
      ];
    });
    const csv = [headers, ...rows].map((r: string[]) =>
      r.map((c: string) => `"${String(c).replaceAll('"', '""')}"`).join(','),
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-trail-${this.ymd(new Date())}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  private ymd(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
}
